export type TrailActor = { kind: 'system' } | { kind: 'unknown' } | { kind: 'person'; name: string }

export type TrailWhen = { kind: 'exact'; at: Date } | { kind: 'onRecordBy'; at: Date }

interface TrailEntryLike {
  at: string
  action: string
  actor_kind: string
  actor_name: string
}

const IMPORTED_COMMENT = 'imported_comment'

/** Branches on `actor_kind`, never on the name. */
export function trailActor(entry: TrailEntryLike): TrailActor {
  if (entry.actor_kind === 'system') return { kind: 'system' }
  if (entry.actor_kind === 'unknown' || entry.action === IMPORTED_COMMENT) return { kind: 'unknown' }
  return { kind: 'person', name: entry.actor_name }
}

interface SignEventLike extends TrailEntryLike {
  stage: string
  verdict: string
}

/** Who signed each stage: the latest person's `sign` event, named only while its verdict is still the stage's status; a `Pending` reopening names nobody. */
export function stageSigners(
  events: readonly SignEventLike[],
  statusOf: (stage: string) => string,
): Record<string, string> {
  const latest: Record<string, SignEventLike> = {}
  for (const entry of events) {
    if (entry.action !== 'sign' || !entry.stage) continue
    const seen = latest[entry.stage]
    if (!seen || new Date(entry.at) >= new Date(seen.at)) latest[entry.stage] = entry
  }
  const signers: Record<string, string> = {}
  for (const [stage, entry] of Object.entries(latest)) {
    const actor = trailActor(entry)
    if (
      actor.kind === 'person' &&
      actor.name &&
      entry.verdict !== 'Pending' &&
      entry.verdict === statusOf(stage)
    ) {
      signers[stage] = actor.name
    }
  }
  return signers
}

/** An imported comment's `at` is when its record was last saved, not when it was written, so it is only a "by" date. */
export function trailWhen(entry: TrailEntryLike): TrailWhen {
  const at = new Date(entry.at)
  if (entry.action === IMPORTED_COMMENT || entry.actor_kind === 'unknown') return { kind: 'onRecordBy', at }
  return { kind: 'exact', at }
}
